// @flow
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import Button from 'components/atoms/button/Button';
import Icon from 'components/_particles/icon/react/Icon';
import classNames from 'classnames';
import { t } from 'javascripts/utils/withTranslation';
import _debounce from 'lodash/debounce';

export type Props = {
  url?: string,
  queryParam?: string,
  inputProps?: {
    value?: string,
    min?: string,
    max?: string,
    placeholder?: string,
    className?: string,
    // when pased directly from the DOM, the class attribute can be used instead of className
    class?: string,
    name?: string,
  },
};

export async function fetchUpdateNumberInputAmount(
  url: string,
  queryParam: string,
  amount: number
): Promise<string | number> {
  if (url) {
    try {
      const response = await fetch(`${url}&${queryParam}=${amount}`);
      if (!response.ok) {
        return -1;
      }

      const json = await response.json();
      return json.toString();
    } catch (error) {
      console.error(error.message);
      return -1;
    }
  }
  return -1;
}

const NumberInput = ({
  url,
  queryParam,
  inputProps: attrs,
}: Props): React.MixedElement => {
  const { class: domClassName, ...inputProps } = attrs || {};
  const [value, setValue] = useState(parseInt(inputProps?.value || 0, 10));
  const [fallbackValue, setFallbackValue] = useState(
    parseInt(inputProps?.value || 0, 10)
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { min, max } = useMemo(
    () => ({
      min: parseInt(inputProps?.min, 10) || 0,
      max: parseInt(inputProps?.max, 10) || 50,
    }),
    [inputProps]
  );

  const fetchUpdate = async (newVal: number) => {
    setLoading(true);

    if (!url || !queryParam) {
      console.warn(
        'Trying to persist value, but url or queryParam is missing in NumberInput!'
      );
      setLoading(false);
      return;
    }
    const req = await fetchUpdateNumberInputAmount(url, queryParam, newVal);

    if (req) {
      setValue(parseInt(req, 10));
      setFallbackValue(parseInt(req, 10));
      setLoading(false);
    }

    if (req === -1 || req === '-1' || !req) {
      setErrorMessage(t('Es ist ein Fehler aufgetreten.'));
      setValue(fallbackValue);
      setLoading(false);
    }
  };

  const debounceFn = useCallback(_debounce(fetchUpdate, 1000), []);

  const getMinMaxValue = (newVal: number): number => {
    if (newVal > max) {
      setErrorMessage(t('Maximalwert erreicht'));
      return max;
    }
    if (newVal < min) {
      setErrorMessage(t('Minimalwert erreicht'));
      return min;
    }
    setErrorMessage(null);
    return newVal;
  };

  const handleChange = (event: SyntheticInputEvent<HTMLInputElement>) => {
    const targetValue = event.target.valueAsNumber;

    if (Number.isNaN(targetValue)) {
      setErrorMessage(t('Ungültiger Wert.'));
      return;
    }

    const setVal = getMinMaxValue(targetValue);
    setValue(setVal);
    debounceFn(setVal);
  };

  const handleClickChange = (newVal: number) => {
    setValue(newVal);
    debounceFn(newVal);
  };

  return (
    <>
      <Button
        type="button"
        className="number-input__button number-input__button--add"
        icon="arrow-up"
        small
        style="chromeless"
        onClick={() => {
          handleClickChange(value + 1);
        }}
        disabled={value === max}
      >
        {t('Anzahl erhöhen')}
      </Button>

      <input
        {...inputProps}
        type="number"
        className={classNames(
          domClassName,
          inputProps?.className,
          'number-input__input'
        )}
        value={value}
        onChange={handleChange}
        disabled={loading}
      />

      <Button
        type="button"
        className="number-input__button number-input__button--remove"
        icon="arrow-down"
        small
        style="chromeless"
        onClick={() => {
          handleClickChange(value - 1);
        }}
        disabled={value === min}
      >
        {t('Anzahl verringern')}
      </Button>

      {errorMessage && (
        <div className="announcement number-input__error-message number-input__max-message">
          {errorMessage}

          <button
            className="button button--chromeless button--small number-input__button number-input__error-close"
            type="button"
            onClick={() => setErrorMessage(null)}
            ref={(node) => {
              node?.focus({ focusVisible: false });
            }}
          >
            <Icon icon="close" className="button__icon" />

            <span className="button__text">{t('Meldung schließen')}</span>
          </button>
        </div>
      )}
      {loading && (
        <div className="number-input__loader">
          <Icon icon="list-loader" className="number-input__loader-icon" />
          <p className="u-hidden-visually">{t('Anzahl wird aktualisiert.')}</p>
        </div>
      )}
    </>
  );
};

export default NumberInput;
